import React, { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputLedyerConfig } from "lib/types/generated/graphql-types"
import { useLazyQuery, useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import { handleErrorMessages } from "helpers/errors"
import { VALIDATE_LEDYER } from "../../../../graphql/queries/config/Providers/Ledyer/ValidateLedyerConfig"
import { CREATE_OR_UPDATE_LEDYER_CONFIG } from "../../../../graphql/mutations/config/Providers/Ledyer/CreateOrUpdateLedyerConfig"
import { LedyerConfigInputs } from "../../../../components/Config/Ledyer/LedyerConfigInputs"

type Props = {
  refetch: () => void
}

interface ExtendedInputLedyerConfig {
  id: string
  ledyerConfig: InputLedyerConfig
}

export const CreateLedyerConfig = ({ refetch }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputLedyerConfig>({ mode: "onBlur" })

  const [addLedyerConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_LEDYER_CONFIG,
    {
      onCompleted: () => {
        reset({
          id: "",
          ledyerConfig: {
            clientId: "",
            clientSecret: "",
            baseUrl: "",
            authUrl: ""
          }
        })
        dispatch(hideEditSidebar())
        dispatch(
          alertActions.actions.createAlert({
            message: "Ledyer configuration successfully created",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const submitToApi = (input: ExtendedInputLedyerConfig) =>
    addLedyerConfig({
      variables: {
        id: input.id,
        ledyerConfig: input.ledyerConfig
      }
    })

  const [validateLedyer, { loading }] = useLazyQuery(VALIDATE_LEDYER, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputLedyerConfig> = (data) => {
    const { clientId, clientSecret, baseUrl, authUrl } = data.ledyerConfig
    validateLedyer({
      variables: {
        clientId,
        clientSecret,
        baseUrl,
        authUrl
      }
    }).then((response) => {
      if (response.data.Configuration.validate.ledyer.valid) {
        submitToApi(data)
      } else {
        setValidationError(true)
      }
    })
  }

  const directSubmit: SubmitHandler<ExtendedInputLedyerConfig> = (data) => {
    submitToApi(data)
    setValidationError(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create ledyer configuration">
          <PrimaryButton type="submit" loading={createLoading || loading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with ledyer. Do you want to save the ledyer config anyway? "
            buttonText="Yes, save"
            handleOkClick={handleSubmit(directSubmit)}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <LedyerConfigInputs control={control} errors={errors} setValue={setValue} />
      </EditSidebar>
    </form>
  )
}
